/** @module Thin HTTP binding for Rust-owned trigger schedules, receipts and confirmation cards. */
import { request } from './api';
import { toFluxAgentResult, type FluxAgentAction } from './fluxAgentApi';

/** A server-formatted upcoming occurrence. */
export interface TriggerOccurrence {
  /** UTC RFC 3339 instant. */
  at: string;
  /** Wall-clock label in the trigger's timezone. */
  label: string;
}

/** Owner-scoped schedule, with timing and recovery policy already evaluated in Rust. */
export interface ScheduledTrigger {
  /** Opaque trigger identifier. */
  id: string;
  /** User-visible title. */
  title: string;
  /** Instruction run at each occurrence. */
  instruction: string;
  /** Plain schedule description from Rust. */
  scheduleText: string;
  /** IANA timezone fixed when the trigger was created. */
  timezone: string;
  /** Completed can include an interrupted one-time run; inspect lastError. */
  status: 'active' | 'paused' | 'completed';
  /** Why it paused, or null. */
  pausedReason: 'person' | 'failures' | null;
  /** Whether push delivery was requested. */
  notify: boolean;
  /** Next three occurrences, empty for inactive schedules. */
  nextRuns: TriggerOccurrence[];
  /** Most recent error or uncertain outcome, or null. */
  lastError: string | null;
  /** Whether a run currently holds a live lease. */
  running: boolean;
  /** Results conversation, or null if it was deleted. */
  conversationId: string | null;
}

/** A persisted occurrence, including explicit unknown-outcome recovery. */
export interface TriggerRun {
  /** Receipt identifier retained through recovery. */
  id: string;
  /** Scheduled UTC instant. */
  scheduledFor: string;
  /** Current receipt state. */
  status: 'running' | 'succeeded' | 'failed' | 'skipped';
  /** Plain reply summary, or null. */
  summary: string | null;
  /** Error or uncertain outcome, or null. */
  error: string | null;
  /** Number of confirmation cards left for the person. */
  pendingCards: number;
}

/** Credentialed route bindings; Resume/Delete only stage cards, whose Confirm uses fluxAgentApi. */
export const fluxAgentTriggersApi = {
  /** Fetch the current person's schedules. */
  list: async () =>
    toFluxAgentResult(await request<{ triggers: ScheduledTrigger[] }>('/flux-agent/triggers')),
  /** Fetch occurrence receipts for one owned trigger. */
  runs: async (id: string) =>
    toFluxAgentResult(
      await request<{ runs: TriggerRun[] }>(`/flux-agent/triggers/${encodeURIComponent(id)}/runs`)
    ),
  /** Pause future work and invalidate the current lease. */
  pause: async (id: string) =>
    toFluxAgentResult(
      await request<{ trigger: ScheduledTrigger }>(
        `/flux-agent/triggers/${encodeURIComponent(id)}/pause`,
        { method: 'POST' }
      )
    ),
  /** Stage a confirmation card; this request does not resume or delete the schedule. */
  stage: async (id: string, action: 'resume' | 'delete') =>
    toFluxAgentResult(
      await request<{ action: FluxAgentAction }>(
        `/flux-agent/triggers/${encodeURIComponent(id)}/${action}`,
        { method: 'POST' }
      )
    ),
};
